import {Component} from '@angular/core';
import {LanguageService} from '../../../shared/services/language.service';

@Component({
    selector: 'course-enroll-students-support-article',
    template: `
        <ng-container i18n="@@support.articles.enrollStudents">
            <p>Students should always be enrolled in a class. Don't have a class yet? Then create one first.</p>
            <p>
                <a routerLink="support/organizations/classes/add" mat-stroked-button color="primary" class="supportLink">Add a class</a>
            </p>
            <ol>
                <li>
                    <span>Click in the <a routerLink="/organization/classes">Classes Overview</a> on the desired class.</span>
                </li>
                <li>
                    <span>Click the <strong>Add students</strong> button.</span>
                </li>
                <li>
                    <div>
                        <p>Choose your preferred method to add students:</p>

                        <mat-accordion class="example-headers-align">
                            <mat-expansion-panel [expanded]="selectedMethod === 'single'" (opened)="setMethod('single')" hideToggle>
                                <mat-expansion-panel-header>
                                    <mat-panel-title>
                                        <img src="assets/images/icons/addOneStudent.svg" class="svgIcon enrollTypeIcon">
                                        Add one student
                                    </mat-panel-title>
                                </mat-expansion-panel-header>

                                <p>Use this option when you only want to add a single student to a class.</p>
                                <p>Fill in the fields:</p>
                                <ul>
                                    <li>
                                        <span>
                                            The student's <strong>first name</strong>
                                        </span>
                                    </li>
                                    <li>
                                        <span>
                                            The student's <strong>name</strong>
                                        </span>
                                    </li>
                                    <li>
                                        <span>
                                            <strong>E-mail:</strong> this is an optional field. If the e-mail address is known, we can send the login details directly to the student. If the e-mail address is not filled in, the administrator must provide the login details to the students.
                                        </span>
                                    </li>
                                </ul>
                                <p>
                                    <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/addOneStudent.png" />
                                </p>
                            </mat-expansion-panel>

                            <mat-expansion-panel [expanded]="selectedMethod === 'multi'" (opened)="setMethod('multi')" hideToggle>
                                <mat-expansion-panel-header>
                                    <mat-panel-title>
                                        <img src="assets/images/icons/addManyStudents.svg" class="svgIcon enrollTypeIcon">
                                        Add multiple students
                                    </mat-panel-title>
                                </mat-expansion-panel-header>

                                <p>With this option dozens of students can be added at once. Fill in one row in the grid for each student. Only the name and first name of the student are mandatory, the e-mail address is optional.</p>
                                <p>When all data has been entered, click <strong>Save</strong> to add the students.</p>
                                <p>
                                    <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/addMultipleStudents.png" />
                                </p>

                                <h3>Copy and paste from Excel</h3>
                                <p>It is possible to copy a student list from Excel and paste it into the grid. This makes it child's play to add all your students with one action.</p>
                                <ol>
                                    <li>
                                        <span>Make sure the columns of your student list in Excel are in the correct order: Name, First name, Email (optional)</span>
                                    </li>
                                    <li>
                                        <span>
                                            Select the data in Excel. Note: <strong>only select the Name, First name and E-mail columns!</strong> The headers of the columns should not be selected.
                                            <br />
                                            <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/excelSelection.png" />
                                        </span>
                                    </li>
                                    <li>
                                        <span>Copy the selection (keyboard shortcut CTRL+C)</span>
                                    </li>
                                    <li>
                                        <span>
                                            Now go back to the grid on the Tyyp website and click on the first cell to select it.
                                            <br />
                                            <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/firstCell.png" />
                                        </span>
                                    </li>
                                    <li>
                                        <span>
                                            Paste the copied data into the grid (shortcut CTRL+V). The student data now appears in the correct place in the grid.
                                            <br />
                                            <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/gridPasted.png" />
                                            <br />
                                            Tip: new rows are automatically added to the grid as necessary. So you can add as many students in the grid as you want!
                                        </span>
                                    </li>
                                    <li>
                                        <span>Click <strong>Save</strong> at the bottom to add the students to the class.</span>
                                    </li>
                                </ol>
                            </mat-expansion-panel>

                            <mat-expansion-panel [expanded]="selectedMethod === 'selfJoin'" (opened)="setMethod('selfJoin')" hideToggle>
                                <mat-expansion-panel-header>
                                    <mat-panel-title>
                                        <img src="assets/images/icons/selfJoin.svg" class="svgIcon enrollTypeIcon">
                                        Let students self-enroll
                                    </mat-panel-title>
                                </mat-expansion-panel-header>

                                <p>This option allows students to register their own account. They are automatically added to this class after registration.</p>
                                <p>
                                    <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/addStudentsSelfJoin.png" />
                                </p>

                                <ol>
                                    <li>
                                        <span>Copy the registration link. Every class has a unique link.</span>
                                    </li>
                                    <li>
                                        <span>Share the registration link with your students.</span>
                                    </li>
                                    <li>
                                        <span>Each student can register via the registration link. After registration the students will appear in the student list of the class.</span>
                                    </li>
                                </ol>
                            </mat-expansion-panel>
                        </mat-accordion>
                    </div>
                </li>
                <li>
                    <div>
                        <p>After enrolling your students, a window appears in which you can purchase the required amount of licenses for these students.</p>
                        <p>The licenses are automatically assigned to the students and activated after payment.</p>
                        <p>
                            <a
                                routerLink="/support/organizations/licenses/enroll-order"
                                mat-stroked-button
                                color="primary"
                                class="supportLink"
                            >Purchase licenses when enrolling students</a>
                        </p>
                    </div>
                </li>
                <li>
                    <div>
                        <p>After completing the order you can send the login details to your students.</p>
                        <p>We provide three useful tools for this:</p>
                        <ul>
                            <li>
                                <strong>Student list:</strong> Here you will find a clear list of all the students you have just added.
                            </li>
                            <li>
                                <strong>Login cards:</strong> Print one card per student. The card contains all the instructions and login details that a student needs to get started.
                            </li>
                            <li>
                                <strong>Email your students:</strong> This button sends an e-mail with login details to each student. This option is only available if you have entered the e-mail address.
                            </li>
                        </ul>
                        <p>
                            <img src="assets/images/support/{{ languageService.getActiveLanguageCode() }}/organizationOrderConfirmation.png" />
                        </p>
                    </div>
                </li>
            </ol>
        </ng-container>
    `
})
export class EnrollStudentsSupportArticleComponent {
    selectedMethod = '';
    constructor(
        public languageService: LanguageService
    ) {}

    setMethod(method: string) {
        this.selectedMethod = method;
    }
}
